import {
  type ConvertedPiece,
  convertPieces,
  type Dictionary,
} from "@kensio/pinyinjs";

import { wireCopyButton } from "./copy-button.js";
import { copyFlavoursOf } from "./copy-html.js";
import { debounced } from "./debounce.js";
import { wireExamples } from "./examples.js";
import { findElements, type ToolElements } from "./hanzi-elements.js";
import { currentState, startingState, type Startup } from "./hanzi-state.js";
import { showConversion } from "./output.js";
import type { ServedTier } from "./dictionary.js";
import { wireShareLink } from "./share-link.js";
import { rememberUrl } from "./url-state.js";
import { wireTranscriptionCopies } from "./transcription-panels.js";

/**
 * The hanzi input tool: Chinese characters in, pinyin and everything else out.
 *
 * The one page here that needs a dictionary, and therefore the one with a
 * loading state. It opens on the `standard` tier and offers `full` as a
 * deliberate upgrade rather than fetching 2.6 MB from everyone who arrives;
 * see `dictionary.ts` for what the difference buys.
 *
 * The exception is somebody who took the offer before: see `TierLoading`.
 */

/**
 * Start the tool on a container the template marked for it.
 *
 * Returns without doing anything if the container is not the shape this
 * expects or does not say where the dictionary is, which is what keeps a
 * markup change from throwing on every page the bundle loads on.
 */
export function startHanziTool(container: HTMLElement): void {
  const dictionaryUrl = container.dataset["dictionary"];
  const elements = findElements(container);

  if (dictionaryUrl === undefined || elements === undefined) {
    return;
  }

  new HanziTool(container, elements, dictionaryUrl).start();
}

/**
 * The tool's state, which is a dictionary, a set of settings and a text.
 */
class HanziTool {
  readonly #container: HTMLElement;
  readonly #elements: ToolElements;
  readonly #tiers: Startup["tiers"];
  readonly #settings: Startup["settings"];

  readonly #scheduleConvert = debounced(() => {
    this.#convert();
  });

  /** The tier a link asked for, where it asked for one. */
  readonly #opensOn: ServedTier | undefined;

  #dictionary: Dictionary | undefined;
  #pieces: readonly ConvertedPiece[] = [];

  constructor(
    container: HTMLElement,
    elements: ToolElements,
    dictionaryUrl: string,
  ) {
    this.#container = container;
    this.#elements = elements;

    const startup = startingState(elements, dictionaryUrl, () => {
      this.#convert();
    });
    this.#tiers = startup.tiers;
    this.#settings = startup.settings;
    this.#opensOn = startup.tier;
  }

  /**
   * Take a dictionary and convert with it.
   *
   * Everything about the loading — the status line, the upgrade button, what
   * happens when the fetch fails — is `TierLoading`'s. All this has to know is
   * whether a dictionary came back.
   */
  async #load(tier: ServedTier): Promise<void> {
    const dictionary = await this.#tiers.load(tier);

    if (dictionary !== undefined) {
      this.#dictionary = dictionary;
      this.#convert();
    }
  }

  #convert(): void {
    const dictionary = this.#dictionary;
    if (dictionary === undefined) {
      return;
    }

    const text = this.#elements.input.value;
    this.#pieces =
      text.trim() === ""
        ? []
        : convertPieces(dictionary, text, this.#settings.convertOptions);

    showConversion(
      this.#elements.output,
      this.#elements.transcriptions,
      this.#pieces,
    );
    rememberUrl(currentState(this.#elements, this.#settings, this.#tiers));
  }

  /** Say something, settle which dictionary this visit wants, and load it. */
  async #open(): Promise<void> {
    await this.#load(await this.#tiers.opening(this.#opensOn));
  }

  start(): void {
    const { input, copy } = this.#elements;

    input.addEventListener("input", this.#scheduleConvert);

    if (copy !== undefined) {
      // Nothing to copy before anything has been converted.
      wireCopyButton(copy, () =>
        this.#pieces.length === 0
          ? undefined
          : copyFlavoursOf(this.#pieces, this.#container),
      );
    }

    wireShareLink(this.#container, () =>
      currentState(this.#elements, this.#settings, this.#tiers),
    );

    this.#tiers.wireOffers(async (tier) => this.#load(tier));

    wireExamples(this.#container, (text) => {
      input.value = text;
      this.#convert();
    });

    wireTranscriptionCopies(this.#elements.transcriptions);

    void this.#open();
  }
}
